/**
 * API functions for Sequence Definitions
 */

import { auth } from '../firebase';
import type {
  SequenceDefinition,
  SequenceDefinitionListItem,
  SequenceDefinitionCreate,
  SequenceDefinitionUpdate,
} from '@/types/sequence';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * List all sequence definitions (summary view)
 */
export async function listSequenceDefinitions(): Promise<{
  definitions: SequenceDefinitionListItem[];
  count: number;
}> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/sequences/definitions`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to list sequence definitions: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single sequence definition with full details
 */
export async function getSequenceDefinition(id: string): Promise<SequenceDefinition> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/sequences/definitions/${id}`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to get sequence definition: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new sequence definition
 */
export async function createSequenceDefinition(
  data: SequenceDefinitionCreate
): Promise<SequenceDefinition> {
  const headers = await getAuthHeader();

  // Add step numbers
  const steps = data.steps.map((step, index) => ({
    ...step,
    step_number: index + 1,
  }));

  const response = await fetch(`${API_BASE_URL}/api/sequences/definitions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...data, steps }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to create sequence definition: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing sequence definition
 */
export async function updateSequenceDefinition(
  id: string,
  data: SequenceDefinitionUpdate
): Promise<SequenceDefinition> {
  const headers = await getAuthHeader();

  // Add step numbers if steps are provided
  const payload = { ...data };
  if (data.steps) {
    payload.steps = data.steps.map((step, index) => ({
      ...step,
      step_number: index + 1,
    }));
  }

  const response = await fetch(`${API_BASE_URL}/api/sequences/definitions/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to update sequence definition: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a sequence definition
 */
export async function deleteSequenceDefinition(id: string): Promise<void> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/sequences/definitions/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to delete sequence definition: ${response.statusText}`);
  }
}
